<template>
    <app-modal
        :modal-id="modalId"
        :title="modalTitle"
        :preloader="preloader"
        modal-size="large"
        @submit="submit"
        @close-modal="handleModalHidden"
    >
        <template slot="header">
            <h5 class="modal-title">{{ modalTitle }}</h5>
            <button type="button" class="close outline-none" @click.prevent="requestClose">
                <span><app-icon name="x" /></span>
            </button>
        </template>

        <template slot="body">
            <app-overlay-loader v-if="preloader" />

            <div class="convoy-modal" :class="{ 'loading-opacity': preloader }">
                <div class="convoy-modal__hero mb-4">
                    <div class="convoy-modal__hero-main">
                        <span class="convoy-modal__eyebrow">{{ $t('convoy') }}</span>
                        <h6 class="mb-2">{{ modalTitle }}</h6>
                        <p class="mb-0 text-muted">
                            {{ $t('convoy_management_hint') }}
                        </p>
                    </div>

                    <div class="convoy-modal__hero-meta">
                        <span v-if="inputs.code" class="convoy-modal__chip convoy-modal__chip--neutral">
                            {{ `${$t('convoy_code')}: ${inputs.code}` }}
                        </span>
                        <span class="convoy-modal__chip convoy-modal__chip--accent">
                            {{ `${members.length} ${$t('trips')}` }}
                        </span>
                    </div>
                </div>

                <div class="convoy-modal__summary-grid mb-4">
                    <div class="convoy-modal__summary-card">
                        <span class="convoy-modal__summary-label">{{ $t('client') }}</span>
                        <strong>{{ convoyClientName || '---' }}</strong>
                    </div>
                    <div class="convoy-modal__summary-card">
                        <span class="convoy-modal__summary-label">{{ $t('origin') }}</span>
                        <strong>{{ convoySiteName || '---' }}</strong>
                    </div>
                    <div class="convoy-modal__summary-card">
                        <span class="convoy-modal__summary-label">{{ $t('destination') }}</span>
                        <strong>{{ convoyDestinationName || '---' }}</strong>
                    </div>
                    <div class="convoy-modal__summary-card">
                        <span class="convoy-modal__summary-label">{{ $t('master_truck') }}</span>
                        <strong>{{ masterTruckLabel || '---' }}</strong>
                    </div>
                </div>

                <div class="row">
                    <div class="col-lg-12 mb-4 mb-lg-0">
                        <div class="convoy-modal__panel h-100">
                            <div class="convoy-modal__panel-head">
                                <div>
                                    <h6 class="mb-1">{{ $t('convoy_members') }}</h6>
                                    <p class="mb-0 text-muted">{{ $t('selected_master_truck_hint') }}</p>
                                </div>
                                <span class="badge badge-pill badge-secondary">
                                    {{ members.length }} {{ $t('trips') }}
                                </span>
                            </div>

                            <div v-if="members.length === 0" class="convoy-modal__empty-members">
                                <span class="convoy-modal__empty-icon">
                                    <app-icon name="truck" class="size-24" />
                                </span>
                                <div>
                                    <strong>{{ $t('select_master_truck') }}</strong>
                                    <p class="mb-0 text-muted">{{ $t('start_convoy_by_adding_master') }}</p>
                                </div>
                            </div>

                            <div v-else class="convoy-modal__members-grid">
                                <div
                                    v-for="member in members"
                                    :key="member.trip_id"
                                    class="convoy-modal__member-card"
                                    :class="{ 'is-master': inputs.master_trip_id === member.trip_id }"
                                >
                                    <div class="convoy-modal__member-card-top">
                                        <div class="convoy-modal__member-title">
                                            <strong>{{ member.lot_number || '---' }}</strong>
                                            <small>{{ member.horse || '---' }}</small>
                                        </div>

                                        <div class="convoy-modal__member-badges">
                                            <span class="convoy-modal__badge convoy-modal__badge--position">
                                                {{ `#${member.position_in_convoy || 1}` }}
                                            </span>
                                            <span
                                                v-if="inputs.master_trip_id === member.trip_id"
                                                class="convoy-modal__badge convoy-modal__badge--master"
                                            >
                                                {{ $t('master_truck') }}
                                            </span>
                                        </div>
                                    </div>

                                    <div class="convoy-modal__member-details">
                                        <span v-if="member.product_name">{{ member.product_name }}</span>
                                        <span v-if="member.transporter_name">{{ member.transporter_name }}</span>
                                        <span>{{ memberRouteLabel(member) }}</span>
                                    </div>

                                    <div class="convoy-modal__member-facts">
                                        <span v-if="member.client_name" class="convoy-modal__fact-chip">{{ member.client_name }}</span>
                                        <span v-if="member.status_name" class="convoy-modal__fact-chip" :class="memberStatusClass(member)">
                                            {{ member.status_name }}
                                        </span>
                                    </div>

                                    <div class="convoy-modal__member-actions">
                                        <label class="convoy-modal__radio-option mb-0">
                                            <input
                                                v-model="inputs.master_trip_id"
                                                type="radio"
                                                name="master_trip_id"
                                                :value="member.trip_id"
                                                :disabled="readOnly"
                                            />
                                            <span>
                                                {{ inputs.master_trip_id === member.trip_id ? $t('current_master_truck') : $t('make_master_truck') }}
                                            </span>
                                        </label>

                                        <button
                                            v-if="!readOnly && canRemoveMember(member)"
                                            type="button"
                                            class="btn btn-sm btn-outline-danger"
                                            @click="removeMember(member.trip_id)"
                                        >
                                            <app-icon name="trash" class="size-14 mr-1" />
                                            {{ $t('remove_from_convoy') }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="col-lg-12">
                        <div class="convoy-modal__panel mb-4">
                            <div class="convoy-modal__panel-head">
                                <div>
                                    <h6 class="mb-1">{{ $t('add_to_convoy') }}</h6>
                                    <p class="mb-0 text-muted">{{ $t('convoy_trip_option_hint') }}</p>
                                </div>
                            </div>

                            <div class="form-group mb-3">
                                <label for="convoy-trip-select">{{ $t('available_trucks') }}</label>
                                <div class="convoy-modal__add-control">
                                    <advance-search-select
                                        :key="tripSelectorKey"
                                        id="convoy-trip-select"
                                        :data="tripSelectorConfig"
                                        :value="selectedCandidateTrip"
                                        @input="selectCandidateTrip"
                                    />

                                    <button
                                        v-if="!readOnly"
                                        type="button"
                                        class="btn btn-primary convoy-modal__add-button"
                                        :disabled="!selectedCandidateTrip"
                                        @click="addSelectedTrip"
                                    >
                                        <app-icon name="plus" class="size-16 mr-2" />
                                        {{ $t('add') }}
                                    </button>
                                </div>
                            </div>

                            <div v-if="selectedCandidateTrip" class="convoy-modal__candidate-card mb-3">
                                <strong>{{ optionPrimaryLabel(selectedCandidateTrip) }}</strong>
                                <div class="convoy-modal__candidate-meta">
                                    <span v-if="selectedCandidateTrip.product_name">{{ selectedCandidateTrip.product_name }}</span>
                                    <span v-if="selectedCandidateTrip.transporter_name">{{ selectedCandidateTrip.transporter_name }}</span>
                                    <span>{{ memberRouteLabel(selectedCandidateTrip) }}</span>
                                </div>
                            </div>

                        </div>

                        <div class="convoy-modal__panel">
                            <div class="form-group" v-if="selectedUrl">
                                <label for="convoy-status">{{ $t('convoy_status') }}</label>
                                <select id="convoy-status" v-model="inputs.status" class="form-control" :disabled="readOnly">
                                    <option
                                        v-for="option in statusOptions"
                                        :key="option.id"
                                        :value="option.id"
                                    >
                                        {{ option.value }}
                                    </option>
                                </select>
                            </div>

                            <div class="form-group mb-0">
                                <label for="convoy-notes">{{ $t('notes') }}</label>
                                <textarea
                                    id="convoy-notes"
                                    v-model.trim="inputs.notes"
                                    rows="7"
                                    class="form-control"
                                    :disabled="readOnly"
                                    :placeholder="$t('convoy_notes_placeholder')"
                                ></textarea>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <template slot="footer">
            <button type="button" class="btn btn-secondary mr-4" @click="requestClose">
                {{ readOnly ? $t('close') : $t('cancel') }}
            </button>
            <button
                v-if="!readOnly"
                type="button"
                class="btn btn-primary"
                :disabled="preloader || members.length < 2 || !inputs.master_trip_id"
                @click="submit"
            >
                {{ selectedUrl ? $t('save_changes') : $t('create_convoy') }}
            </button>
        </template>
    </app-modal>
</template>

<script>
import CoreLibrary from '../../../../core/helpers/CoreLibrary';
import AdvanceSearchSelect from '../../../../core/components/input/AdvanceSearchSelect';
import * as actions from '../../../Config/ApiUrl';

export default {
    name: 'ConvoyAddEditModal',
    extends: CoreLibrary,
    components: { AdvanceSearchSelect },
    props: {
        selectedUrl: {
            type: String,
            default: '',
        },
        selectedTrips: {
            type: Array,
            default: () => [],
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        tripTableId: {
            type: String,
            default: 'trips-table',
        },
        convoyTableId: {
            type: String,
            default: 'convoys-table',
        },
    },
    data() {
        return {
            modalId: 'convoy-add-edit-modal',
            preloader: false,
            selectedCandidateTrip: null,
            inputs: {
                code: '',
                master_trip_id: null,
                notes: '',
                status: 'active',
            },
            members: [],
        };
    },
    computed: {
        modalTitle() {
            if (this.readOnly) {
                return this.$t('convoy_details');
            }

            return this.selectedUrl ? this.$t('edit_convoy') : this.$t('new_convoy');
        },
        statusOptions() {
            return [
                { id: 'active', value: this.$t('active') },
                { id: 'paused', value: this.$t('paused') },
                { id: 'completed', value: this.$t('completed') },
                { id: 'dissolved', value: this.$t('dissolved') },
            ];
        },
        memberIds() {
            return this.members.map(({ trip_id }) => Number(trip_id));
        },
        tripSelectorConfig() {
            return {
                id: 'convoy-trip-select',
                disabled: this.readOnly,
                fetchUrl: `${actions.CONVOY}/trip-options`,
                fetchParams: this.tripOptionsParams() || {},
                loadedPerTime: 15,
                listValueField: 'label',
                listClass: 'convoy-modal__trip-options',
                placeholder: this.$t('search_trip'),
            };
        },
        tripSelectorKey() {
            const params = this.tripOptionsParams() || {};

            return `convoy-trip-selector-${params.convoy_id || 0}-${params.trip_id || 0}-${this.memberIds.join('-')}`;
        },
        masterTruck() {
            return this.members.find((member) => Number(member.trip_id) === Number(this.inputs.master_trip_id)) || null;
        },
        masterTruckLabel() {
            return this.masterTruck ? this.optionPrimaryLabel(this.masterTruck) : '';
        },
        convoyClientName() {
            return this.members[0]?.client_name || '';
        },
        convoySiteName() {
            return this.members[0]?.site_name || '';
        },
        convoyDestinationName() {
            return this.members[0]?.destination_name || '';
        },
    },
    methods: {
        requestClose() {
            $('#' + this.modalId).modal('hide');
        },
        handleModalHidden() {
            this.$emit('close-modal');
        },
        submit() {
            if (this.members.length < 2) {
                this.$toastr.e(this.$t('convoy_requires_at_least_two_trips'));
                return;
            }

            if (!this.members.some(({ trip_id }) => Number(trip_id) === Number(this.inputs.master_trip_id))) {
                this.$toastr.e(this.$t('master_trip_must_belong_to_convoy'));
                return;
            }

            this.preloader = true;

            const payload = {
                master_trip_id: Number(this.inputs.master_trip_id),
                trip_ids: this.members.map(({ trip_id }) => Number(trip_id)),
                notes: this.inputs.notes || null,
            };

            if (this.selectedUrl) {
                payload.status = this.inputs.status;
            }

            const request = this.selectedUrl
                ? this.axiosPatch({ url: this.selectedUrl, data: payload })
                : this.axiosPost({ url: actions.CONVOY, data: payload });

            request
                .then((response) => {
                    this.$toastr.s(response.data.message);
                    this.$hub.$emit('reload-' + this.tripTableId);
                    this.$hub.$emit('reload-' + this.convoyTableId);
                    this.requestClose();
                })
                .catch(({ response }) => {
                    this.$toastr.e(response?.data?.message || this.$t('something_went_wrong'));
                })
                .finally(() => {
                    this.preloader = false;
                });
        },
        canRemoveMember(member) {
            return !this.readOnly;
        },
        removeMember(tripId) {
            this.members = this.members.filter((member) => Number(member.trip_id) !== Number(tripId));

            if (Number(this.inputs.master_trip_id) === Number(tripId)) {
                this.inputs.master_trip_id = this.members[0]?.trip_id || null;
            }

            this.resequenceMembers();

            this.selectedCandidateTrip = null;
        },
        selectCandidateTrip(trip) {
            this.selectedCandidateTrip = trip || null;
        },
        addSelectedTrip() {
            if (!this.selectedCandidateTrip) {
                return;
            }

            this.members.push(this.normalizeMember(this.selectedCandidateTrip, this.members.length));

            if (!this.inputs.master_trip_id) {
                this.inputs.master_trip_id = this.members[0]?.trip_id || null;
            }

            this.resequenceMembers();
            this.selectedCandidateTrip = null;
        },
        resequenceMembers() {
            this.members = this.members.map((member, index) => ({
                ...member,
                position_in_convoy: index + 1,
            }));
        },
        normalizeMember(trip, index = 0) {
            return {
                trip_id: Number(trip.trip_id || trip.id),
                client_id: trip.client_id ?? null,
                site_id: trip.site_id ?? null,
                destination_id: trip.destination_id ?? null,
                lot_number: trip.lot_number || '---',
                horse: trip.horse || '---',
                client_name: trip.client_name || '',
                product_name: trip.product_name || '',
                site_name: trip.site_name || '',
                destination_name: trip.destination_name || '',
                transporter_name: trip.transporter_name || '',
                status_name: trip.status_name || '',
                status_type: trip.status_type || '',
                position_in_convoy: trip.position_in_convoy ?? trip.convoy_position_in_convoy ?? index + 1,
                role: trip.role || trip.convoy_role || 'member',
            };
        },
        loadCreateState() {
            const normalizedTrips = (this.selectedTrips || []).map(this.normalizeMember);
            const referenceMember = normalizedTrips[0] || null;

            this.members = referenceMember
                ? normalizedTrips.filter((member) => this.isRouteCompatible(member, referenceMember))
                : [];

            this.resequenceMembers();
            this.inputs.master_trip_id = this.members[0]?.trip_id ?? null;
            this.inputs.notes = '';
            this.inputs.status = 'active';
            this.inputs.code = '';
        },
        loadEditState() {
            this.preloader = true;

            this.axiosGet(this.selectedUrl)
                .then((response) => {
                    const convoy = response.data;
                    this.members = (convoy.members || []).map(this.normalizeMember);
                    const existingTripIds = new Set(this.members.map(({ trip_id }) => Number(trip_id)));
                    const referenceMember = this.members[0] || null;

                    (this.selectedTrips || [])
                        .map(this.normalizeMember)
                        .forEach((member) => {
                            if (
                                !existingTripIds.has(Number(member.trip_id))
                                && (!referenceMember || this.isRouteCompatible(member, referenceMember))
                            ) {
                                this.members.push(member);
                            }
                        });

                    this.resequenceMembers();
                    this.inputs.code = convoy.code || '';
                    this.inputs.master_trip_id = Number(convoy.master_trip_id);
                    this.inputs.notes = convoy.notes || '';
                    this.inputs.status = convoy.status || 'active';
                })
                .catch(({ response }) => {
                    this.$toastr.e(response?.data?.message || this.$t('something_went_wrong'));
                    this.requestClose();
                })
                .finally(() => {
                    this.preloader = false;
                });
        },
        tripOptionsParams() {
            const params = {};

            if (this.selectedUrl) {
                const convoyId = this.convoyIdFromUrl();
                if (!convoyId) {
                    return null;
                }

                params.convoy_id = convoyId;
            } else {
                const referenceTripId = this.members[0]?.trip_id || this.selectedTrips[0]?.id || this.selectedTrips[0]?.trip_id;

                if (referenceTripId) {
                    params.trip_id = referenceTripId;
                }
            }

            if (this.memberIds.length) {
                params.exclude_trip_ids = this.memberIds.join(',');
            }

            return params;
        },
        convoyIdFromUrl() {
            const segments = (this.selectedUrl || '').split('/').filter(Boolean);
            const lastSegment = segments[segments.length - 1];
            const convoyId = Number(lastSegment);

            return Number.isFinite(convoyId) && convoyId > 0 ? convoyId : null;
        },
        optionPrimaryLabel(trip) {
            return [trip.lot_number, trip.horse].filter(Boolean).join(' / ') || '---';
        },
        memberRouteLabel(member) {
            const origin = member.site_name || '---';
            const destination = member.destination_name || '---';

            return `${origin} → ${destination}`;
        },
        memberStatusClass(member) {
            return member.status_type ? `convoy-modal__fact-chip--${member.status_type}` : '';
        },
        isRouteCompatible(member, referenceMember) {
            if (!referenceMember) {
                return true;
            }

            return this.sameRouteValue(member, referenceMember, 'client_id', 'client_name')
                && this.sameRouteValue(member, referenceMember, 'site_id', 'site_name')
                && this.sameRouteValue(member, referenceMember, 'destination_id', 'destination_name');
        },
        sameRouteValue(member, referenceMember, idKey, nameKey) {
            if (member[idKey] !== null && member[idKey] !== undefined
                && referenceMember[idKey] !== null && referenceMember[idKey] !== undefined) {
                return Number(member[idKey]) === Number(referenceMember[idKey]);
            }

            return String(member[nameKey] || '') === String(referenceMember[nameKey] || '');
        },
    },
    created() {
        if (this.selectedUrl) {
            this.loadEditState();
            return;
        }

        this.loadCreateState();
    },
}
</script>

<style scoped>
.convoy-modal__hero {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.1rem 1.2rem;
    border: 1px solid #d9e2f1;
    border-radius: 1.1rem;
    background:
        radial-gradient(circle at top right, rgba(255, 122, 24, 0.14), transparent 38%),
        linear-gradient(135deg, #ffffff, #f7fbff);
}

.convoy-modal__hero-main {
    min-width: 0;
}

.convoy-modal__eyebrow {
    display: inline-flex;
    margin-bottom: 0.35rem;
    color: var(--brand-color, #ff7a18);
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.convoy-modal__hero-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: flex-start;
    justify-content: flex-end;
}

.convoy-modal__chip {
    display: inline-flex;
    align-items: center;
    min-height: 2rem;
    padding: 0.35rem 0.8rem;
    border-radius: 999px;
    border: 1px solid #d9e2f1;
    font-size: 0.82rem;
    font-weight: 600;
}

.convoy-modal__chip--neutral {
    background: rgba(255, 255, 255, 0.92);
    color: #24415f;
}

.convoy-modal__chip--accent {
    border-color: rgba(255, 122, 24, 0.18);
    background: rgba(255, 122, 24, 0.1);
    color: var(--brand-color, #ff7a18);
}

.convoy-modal__summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 0.9rem;
}

.convoy-modal__summary-card,
.convoy-modal__panel,
.convoy-modal__member-card,
.convoy-modal__candidate-card {
    border: 1px solid #d9e2f1;
    border-radius: 1rem;
    background: #fff;
}

.convoy-modal__summary-card {
    display: grid;
    gap: 0.3rem;
    padding: 0.95rem 1rem;
}

.convoy-modal__summary-card strong {
    color: #1d3557;
}

.convoy-modal__summary-label {
    color: #6b7b93;
    font-size: 0.76rem;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
}

.convoy-modal__panel {
    padding: 1rem;
}

.convoy-modal__panel-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1rem;
}

.convoy-modal__members-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 1rem;
}

.convoy-modal__empty-members {
    display: flex;
    align-items: center;
    gap: 1rem;
    min-height: 12rem;
    padding: 1.25rem;
    border: 1px dashed #c9d5e6;
    border-radius: 1rem;
    background: linear-gradient(135deg, #f8fbff, #f3f7fc);
}

.convoy-modal__empty-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 3.5rem;
    width: 3.5rem;
    height: 3.5rem;
    border-radius: 1rem;
    background: rgba(255, 122, 24, 0.12);
    color: var(--brand-color, #ff7a18);
}

.convoy-modal__add-control {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 0.65rem;
}

.convoy-modal__add-button {
    min-width: 7rem;
    white-space: nowrap;
}

.convoy-modal__member-card {
    padding: 1rem;
    transition: border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease;
}

.convoy-modal__member-card.is-master {
    border-color: rgba(255, 122, 24, 0.42);
    box-shadow: 0 0 0 1px rgba(255, 122, 24, 0.18);
    background:
        linear-gradient(135deg, rgba(255, 122, 24, 0.08), rgba(255, 122, 24, 0.02)),
        #fff;
}

.convoy-modal__member-card:hover {
    transform: translateY(-1px);
}

.convoy-modal__member-card-top,
.convoy-modal__member-actions {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.75rem;
}

.convoy-modal__member-title {
    display: grid;
    gap: 0.18rem;
}

.convoy-modal__member-title strong {
    color: #18324a;
}

.convoy-modal__member-title small,
.convoy-modal__member-details,
.convoy-modal__candidate-meta {
    color: #6b7b93;
}

.convoy-modal__member-badges,
.convoy-modal__member-facts {
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem;
}

.convoy-modal__badge,
.convoy-modal__fact-chip {
    display: inline-flex;
    align-items: center;
    min-height: 1.85rem;
    padding: 0.28rem 0.62rem;
    border-radius: 999px;
    font-size: 0.76rem;
    font-weight: 700;
}

.convoy-modal__badge--position,
.convoy-modal__fact-chip {
    border: 1px solid #dde6f4;
    background: #f6f9fd;
    color: #48617f;
}

.convoy-modal__badge--master {
    border: 1px solid rgba(255, 122, 24, 0.18);
    background: rgba(255, 122, 24, 0.12);
    color: var(--brand-color, #ff7a18);
}

.convoy-modal__fact-chip--success {
    border-color: rgba(22, 163, 74, 0.14);
    background: rgba(22, 163, 74, 0.1);
    color: #166534;
}

.convoy-modal__fact-chip--warning {
    border-color: rgba(245, 158, 11, 0.18);
    background: rgba(245, 158, 11, 0.12);
    color: #b45309;
}

.convoy-modal__fact-chip--danger {
    border-color: rgba(220, 38, 38, 0.14);
    background: rgba(220, 38, 38, 0.1);
    color: #b91c1c;
}

.convoy-modal__fact-chip--secondary,
.convoy-modal__fact-chip--info,
.convoy-modal__fact-chip--primary {
    border-color: rgba(59, 130, 246, 0.14);
    background: rgba(59, 130, 246, 0.08);
    color: #1d4ed8;
}

.convoy-modal__member-details,
.convoy-modal__candidate-meta {
    display: grid;
    gap: 0.2rem;
    margin-top: 0.85rem;
    font-size: 0.9rem;
}

.convoy-modal__member-facts {
    margin-top: 0.85rem;
}

.convoy-modal__member-actions {
    margin-top: 1rem;
    padding-top: 0.9rem;
    border-top: 1px dashed #dce5f1;
    align-items: center;
}

.convoy-modal__radio-option {
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
    color: #24415f;
    font-weight: 600;
    cursor: pointer;
}

.convoy-modal__candidate-card {
    padding: 0.9rem 1rem;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.06), rgba(59, 130, 246, 0.02));
}

@media (max-width: 991.98px) {
    .convoy-modal__hero,
    .convoy-modal__member-card-top,
    .convoy-modal__member-actions {
        flex-direction: column;
    }

    .convoy-modal__hero-meta {
        justify-content: flex-start;
    }

    .convoy-modal__add-control {
        grid-template-columns: 1fr;
    }
}
</style>
